import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Bell, X, Trash2, Mail, MailOpen, Clock, AlertTriangle, ChevronRight, Volume2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import api from '../../services/api';

const PRIORITY_STYLES = {
  urgent: 'border-l-red-500 bg-red-50 dark:bg-red-900/10',
  high: 'border-l-orange-500 bg-orange-50 dark:bg-orange-900/10',
  normal: 'border-l-blue-500 bg-blue-50/50 dark:bg-blue-900/5',
  low: 'border-l-slate-300 dark:border-l-slate-600',
};

const PRIORITY_BADGE = {
  urgent: 'bg-red-500/20 text-red-600 border-red-500/30',
  high: 'bg-orange-500/20 text-orange-600 border-orange-500/30',
  normal: 'bg-blue-500/20 text-blue-600 border-blue-500/30',
  low: 'bg-slate-500/20 text-slate-500 border-slate-500/30',
};

const timeAgo = (dateStr) => {
  if (!dateStr) return '';
  const diff = (Date.now() - new Date(dateStr).getTime()) / 1000;
  if (diff < 60) return 'Just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

/* ── Message Detail Modal ── */
const NotificationModal = ({ notification, onClose, onDelete, isDark }) => {
  if (!notification) return null;
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className={`relative rounded-2xl shadow-2xl w-full max-w-lg mx-4 p-6 ${isDark ? 'bg-slate-900 border border-white/10' : 'bg-white'}`} data-testid="notification-modal">
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <Badge className={`mb-2 ${PRIORITY_BADGE[notification.priority] || PRIORITY_BADGE.normal}`}>
              {notification.priority}
            </Badge>
            <h2 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>{notification.title}</h2>
            <p className={`text-xs mt-1 ${isDark ? 'text-white/40' : 'text-slate-400'}`}>
              From {notification.sent_by_name || notification.sent_by} &middot; {timeAgo(notification.created_at)}
            </p>
          </div>
          <button onClick={onClose} className={`p-1 rounded-lg ${isDark ? 'hover:bg-white/10' : 'hover:bg-gray-100'}`}>
            <X className={`w-5 h-5 ${isDark ? 'text-white/60' : 'text-slate-500'}`} />
          </button>
        </div>
        <div className={`prose prose-sm max-w-none mb-6 ${isDark ? 'text-white/80 prose-invert' : 'text-slate-700'}`}>
          <p className="whitespace-pre-wrap">{notification.message}</p>
        </div>
        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => { onDelete(notification.id); onClose(); }}
            className="text-red-500 border-red-500/30 hover:bg-red-500/10"
            data-testid="delete-notification-btn"
          >
            <Trash2 className="w-4 h-4 mr-1" /> Delete
          </Button>
          <Button size="sm" onClick={onClose}>Close</Button>
        </div>
      </div>
    </div>
  );
};

/* ── Notification Slideout (Right Side) ── */
const NotificationSlideout = ({ notifications, onClose, onRead, onDelete, onSelect, isDark, monitor, onAcknowledge }) => {
  const downSites = monitor?.down_sites || [];
  return (
    <div className="fixed inset-0 z-50" data-testid="notification-slideout">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className={`absolute right-0 top-0 h-full w-full max-w-sm shadow-2xl transform transition-transform duration-300 ${isDark ? 'bg-slate-900 border-l border-white/10' : 'bg-white border-l border-gray-200'}`}>
        <div className={`flex items-center justify-between p-4 border-b ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
          <h3 className={`font-bold text-lg ${isDark ? 'text-white' : 'text-slate-900'}`}>Notifications</h3>
          <button onClick={onClose} className={`p-1 rounded-lg ${isDark ? 'hover:bg-white/10' : 'hover:bg-gray-100'}`}>
            <X className={`w-5 h-5 ${isDark ? 'text-white/60' : 'text-slate-500'}`} />
          </button>
        </div>
        {downSites.length > 0 && (
          <div className="m-3 p-3 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-900/50" data-testid="monitor-down-banner">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-4 h-4 text-red-500 animate-pulse" />
              <p className="font-semibold text-sm text-red-700 dark:text-red-400">
                {downSites.length} site{downSites.length > 1 ? 's' : ''} down
              </p>
            </div>
            <ul className="space-y-1 mb-2">
              {downSites.map((s) => (
                <li key={s.id} className="text-xs text-red-600 dark:text-red-300 truncate">• {s.name} — {s.url}</li>
              ))}
            </ul>
            {monitor?.alert_active && (
              <Button size="sm" onClick={onAcknowledge} className="w-full bg-red-600 hover:bg-red-700 text-white" data-testid="acknowledge-downtime-btn">
                Acknowledge & Silence Alarm
              </Button>
            )}
          </div>
        )}
        <div className="overflow-y-auto" style={{ height: 'calc(100% - 65px)' }}>
          {notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20">
              <Bell className={`w-12 h-12 mb-3 ${isDark ? 'text-white/20' : 'text-slate-200'}`} />
              <p className={`text-sm ${isDark ? 'text-white/40' : 'text-slate-400'}`}>No notifications</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100 dark:divide-white/5">
              {notifications.map((n) => (
                <div
                  key={n.id}
                  onClick={() => { if (!n.is_read) onRead(n.id); onSelect(n); }}
                  className={`p-4 cursor-pointer transition-colors border-l-4 ${PRIORITY_STYLES[n.priority] || ''} ${
                    !n.is_read ? (isDark ? 'bg-white/5' : 'bg-blue-50/50') : ''
                  } ${isDark ? 'hover:bg-white/5' : 'hover:bg-gray-50'}`}
                  data-testid={`notification-item-${n.id}`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 ${
                      !n.is_read ? 'bg-orange-500/20' : (isDark ? 'bg-white/5' : 'bg-gray-100')
                    }`}>
                      {!n.is_read ? <Mail className="w-4 h-4 text-orange-500" /> : <MailOpen className={`w-4 h-4 ${isDark ? 'text-white/30' : 'text-slate-400'}`} />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <p className={`font-semibold text-sm truncate ${!n.is_read ? (isDark ? 'text-white' : 'text-slate-900') : (isDark ? 'text-white/60' : 'text-slate-500')}`}>
                          {n.title}
                        </p>
                        <ChevronRight className={`w-4 h-4 flex-shrink-0 ${isDark ? 'text-white/20' : 'text-slate-300'}`} />
                      </div>
                      <p className={`text-xs mt-0.5 truncate ${isDark ? 'text-white/40' : 'text-slate-400'}`}>
                        {n.message?.substring(0, 80)}{n.message?.length > 80 ? '...' : ''}
                      </p>
                      <p className={`text-[10px] mt-1 flex items-center gap-1 ${isDark ? 'text-white/30' : 'text-slate-300'}`}>
                        <Clock className="w-3 h-3" /> {timeAgo(n.created_at)}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/* ── Notification Bell Component ── */
const NotificationBell = ({ isDark }) => {
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [showSlideout, setShowSlideout] = useState(false);
  const [selectedNotif, setSelectedNotif] = useState(null);
  const [monitor, setMonitor] = useState({ any_down: false, alert_active: false, down_sites: [] });
  const audioRef = useRef(null);
  const prevCountRef = useRef(0);
  const audioCtxRef = useRef(null);
  const beepIntervalRef = useRef(null);

  const playBeep = useCallback(() => {
    try {
      if (!audioCtxRef.current) {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx) return;
        audioCtxRef.current = new Ctx();
      }
      const ctx = audioCtxRef.current;
      if (ctx.state === 'suspended') ctx.resume();
      [0, 0.28, 0.56].forEach((t) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'square';
        osc.frequency.value = 880;
        gain.gain.setValueAtTime(0.0001, ctx.currentTime + t);
        gain.gain.exponentialRampToValueAtTime(0.2, ctx.currentTime + t + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + t + 0.18);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(ctx.currentTime + t);
        osc.stop(ctx.currentTime + t + 0.2);
      });
    } catch (e) { /* ignore */ }
  }, []);

  const fetchMonitor = useCallback(async () => {
    try {
      const res = await api.get('/website-monitor/status');
      setMonitor(res.data || { any_down: false, alert_active: false, down_sites: [] });
    } catch (e) { /* silent (support/admin only) */ }
  }, []);

  const handleAcknowledge = async () => {
    try {
      await api.post('/website-monitor/acknowledge');
      setMonitor((m) => ({ ...m, alert_active: false }));
    } catch (e) { /* silent */ }
  };

  const fetchCount = useCallback(async () => {
    try {
      const res = await api.get('/notifications/unread-count');
      const newCount = res.data?.count || 0;
      if (newCount > prevCountRef.current && prevCountRef.current >= 0) {
        // Play sound on new notification
        try {
          if (audioRef.current) {
            audioRef.current.currentTime = 0;
            audioRef.current.play().catch(() => {});
          }
          // Desktop notification
          if (Notification.permission === 'granted') {
            new Notification('A2G Designs', {
              body: 'You have a new notification',
              icon: '/icons/icon-192x192.png',
              tag: 'a2g-new-notif',
            });
          }
        } catch (e) { /* ignore audio errors */ }
      }
      prevCountRef.current = newCount;
      setUnreadCount(newCount);
    } catch (e) { /* silent */ }
  }, []);

  const fetchNotifications = async () => {
    try {
      const res = await api.get('/notifications/my-notifications');
      setNotifications(res.data?.notifications || []);
    } catch (e) { /* silent */ }
  };

  useEffect(() => {
    // Request notification permission
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
    fetchCount();
    fetchMonitor();
    const interval = setInterval(fetchCount, 30000); // poll every 30s
    const monInterval = setInterval(fetchMonitor, 30000);
    // Unlock Web Audio on first user interaction (browser autoplay policy)
    const unlock = () => {
      try {
        if (!audioCtxRef.current) {
          const Ctx = window.AudioContext || window.webkitAudioContext;
          if (Ctx) audioCtxRef.current = new Ctx();
        }
        if (audioCtxRef.current?.state === 'suspended') audioCtxRef.current.resume();
      } catch (e) { /* ignore */ }
    };
    window.addEventListener('click', unlock, { once: true });
    window.addEventListener('keydown', unlock, { once: true });
    return () => {
      clearInterval(interval);
      clearInterval(monInterval);
      window.removeEventListener('click', unlock);
      window.removeEventListener('keydown', unlock);
    };
  }, [fetchCount, fetchMonitor]);

  // Recurring beep every minute while downtime is unacknowledged
  useEffect(() => {
    if (monitor.alert_active) {
      playBeep();
      if (!beepIntervalRef.current) {
        beepIntervalRef.current = setInterval(playBeep, 60000);
      }
    } else if (beepIntervalRef.current) {
      clearInterval(beepIntervalRef.current);
      beepIntervalRef.current = null;
    }
    return () => {};
  }, [monitor.alert_active, playBeep]);

  const handleBellClick = () => {
    fetchNotifications();
    setShowSlideout(true);
  };

  const handleRead = async (id) => {
    try {
      await api.put(`/notifications/${id}/read`);
      setNotifications((prev) => prev.map((n) => n.id === id ? { ...n, is_read: true } : n));
      setUnreadCount((c) => Math.max(0, c - 1));
    } catch (e) { /* silent */ }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/notifications/${id}`);
      setNotifications((prev) => prev.filter((n) => n.id !== id));
      fetchCount();
    } catch (e) { /* silent */ }
  };

  return (
    <>
      <audio ref={audioRef} src="/a2g-notification-alert.mp3" preload="auto" />
      <button
        onClick={handleBellClick}
        className={`relative p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-white/10' : 'hover:bg-gray-100'}`}
        data-testid="notification-bell"
      >
        <Bell className={`w-5 h-5 ${monitor.any_down ? 'text-red-500' : (isDark ? 'text-white/60' : 'text-slate-600')}`} />
        {monitor.any_down && (
          <span
            className={`absolute -bottom-0.5 -left-0.5 w-3 h-3 rounded-full bg-red-500 ring-2 ring-white dark:ring-slate-800 ${monitor.alert_active ? 'animate-ping' : ''}`}
            data-testid="monitor-down-light"
          />
        )}
        {monitor.any_down && (
          <span className="absolute -bottom-0.5 -left-0.5 w-3 h-3 rounded-full bg-red-500 ring-2 ring-white dark:ring-slate-800" />
        )}
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center text-[10px] font-bold text-white bg-red-500 rounded-full px-1 animate-pulse" data-testid="unread-badge">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {showSlideout && (
        <NotificationSlideout
          notifications={notifications}
          onClose={() => setShowSlideout(false)}
          onRead={handleRead}
          onDelete={handleDelete}
          onSelect={(n) => { if (!n.is_read) handleRead(n.id); setSelectedNotif(n); }}
          isDark={isDark}
          monitor={monitor}
          onAcknowledge={handleAcknowledge}
        />
      )}

      {selectedNotif && (
        <NotificationModal
          notification={selectedNotif}
          onClose={() => setSelectedNotif(null)}
          onDelete={handleDelete}
          isDark={isDark}
        />
      )}
    </>
  );
};

export default NotificationBell;
